import React, { useRef, useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Nav from "./components/Nav";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import Household from "./pages/Household";
import Industrial from "./pages/Industrial";
import ProductsPage from "./pages/ProductsPage";
import Blog from "./pages/Blog";
import About from "./pages/About";
import ProductDetail from "./pages/ProductDetail";
import LoadingScreen from "./components/LoadingScreen";
import { fetchProducts } from "./contentful";
import ScrollToTop from "./components/ScrollToTop";
import ScrollLock from "./components/ScrollLock";
import { Analytics } from "@vercel/analytics/react"
import { CONTENT_TYPES } from "./constants/contentTypes";
import Navbar from './components/Navbar';

const AppContent = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [householdProducts, setHouseholdProducts] = useState([]);
  const [professionalProducts, setProfessionalProducts] = useState([]);
  const footerRef = useRef(null);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleLoad = () => {
      setIsLoading(false);
    };

    if (document.readyState === "complete") {
      handleLoad();
    } else {
      window.addEventListener("load", handleLoad);
      return () => window.removeEventListener("load", handleLoad);
    }
  }, []);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        // Load household products
        const householdFetchedProducts = await fetchProducts(CONTENT_TYPES.HOUSEHOLD);
        setHouseholdProducts(householdFetchedProducts);

        // Load professional products
        const professionalFetchedProducts = await fetchProducts(CONTENT_TYPES.PROFESSIONAL);
        setProfessionalProducts(professionalFetchedProducts);
      } catch (error) {
        console.error("Error in loadProducts:", error);
      }
    };

    loadProducts();
  }, []);

  const getProductsByType = (type) => {
    return type === 'professional' ? professionalProducts : householdProducts;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <ScrollLock>
      <LoadingScreen isLoading={isLoading} />
      {!isLoading && (
        <>
          {!isHomePage && (
            <div className="fixed top-0 left-0 right-0 z-50">
              <div className="relative z-50">
                <Navbar />
              </div>
              <div className="relative z-40 shadow-[0_8px_16px_rgba(0,0,0,0.3)]">
                <Nav />
              </div>
            </div>
          )}
          <div className={isHomePage ? "" : "pt-32"}>
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/household" element={<Navigate to="/household/home" replace />} />
              <Route path="/industrial" element={<Navigate to="/industrial/home" replace />} />
              <Route path="/household/home" element={<Household footerRef={footerRef} />} />
              <Route path="/industrial/home" element={<Industrial footerRef={footerRef} />} />
              <Route path="/:type/products" element={<ProductsPage />} />
              <Route path="/:type/blog" element={<Blog />} />
              <Route path="/:type/about" element={<About />} />
              <Route 
                path="/:type/product/:productTitle" 
                element={<ProductDetail products={getProductsByType} />} 
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            {!isHomePage && (
              <div ref={footerRef}>
                <Footer />
              </div>
            )}
          </div>
        </>
      )}
      <Analytics />
    </ScrollLock>
  );
};

const App = () => {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
};

export default App;